import styled from "styled-components";
import { FormControl, FormControlLabel, Radio, RadioGroup } from "@mui/material";
import React, { useState } from "react";

import { CheckBoxComponent, StyledChecker, StyledButton, ClipLoaderCommon } from "@components";
import { breakpoint } from "@utilities";
import { PAYMENT_OPTION, SHARE_OPTION } from "@constants";
import { ICartItemData } from "@interfaces";
import { ErrorToast } from "src/Components/toasts/ErrorToast";
import { Link } from "react-router-dom";

interface IPaymentOptionProps {
    handleOrderCreate: (paymentType: string) => Promise<void>;
    onOrderCreateSuccess?: () => void;
}

export const PaymentOptionComponent = (props: IPaymentOptionProps) => {
    const { handleOrderCreate, onOrderCreateSuccess } = props;

    const [paymentOption, setPaymentOption] = useState(PAYMENT_OPTION);
    const [isAgreeTerm, setIsAgreeTerm] = useState<boolean>(false);
    const [isOrderInProgress, setOrderInProgress] = useState<boolean>(false);

    const onChangePaymentOption = (event: React.ChangeEvent<HTMLInputElement>) => {
        setPaymentOption((event.target as HTMLInputElement).value);
    };

    const handleCreateOrder = async (paymentType: string) => {
        const createOrderFailedmessage = "Failed to create order";
        try {
            setOrderInProgress(true);
            await handleOrderCreate(paymentType);
            if (onOrderCreateSuccess) {
                onOrderCreateSuccess();
            }
        } catch (error) {
            console.error(error);
            ErrorToast({ Message: createOrderFailedmessage });
        } finally {
            setOrderInProgress(false);
        }
    };

    const handleToggleAgree = () => {
        setIsAgreeTerm(!isAgreeTerm);
    };

    return (
        <div className="payment-wrapper">
            <ListOptions>
                <FormControl component="fieldset">
                    <RadioGroup
                        aria-label="payment"
                        name="payment"
                        value={paymentOption}
                        onChange={onChangePaymentOption}
                    >
                        <FormControlLabel
                            value={PAYMENT_OPTION}
                            control={<Radio />}
                            label="By credit card"
                        />
                    </RadioGroup>
                </FormControl>

                <div className="checkBox">
                    <StyledChecker>
                        <CheckBoxComponent
                            id="check"
                            isChecked={isAgreeTerm}
                            onChange={handleToggleAgree}
                        />
                    </StyledChecker>
                    <p className="checkBoxText">
                        I accept the{" "}
                        <Link to="/booking-terms-and-conditions" target="_blank" rel="noreferrer">
                            booking terms and conditions.
                        </Link>
                    </p>
                </div>

                {paymentOption === PAYMENT_OPTION ? (
                    <PaymentButton
                        $borderRadius="4px"
                        color={["#436CB2 0%", "#28B7C3 100%"]}
                        onClick={() => handleCreateOrder("pay-now")}
                        disabled={!isAgreeTerm || isOrderInProgress}
                    >
                        {isOrderInProgress ? (
                            <ClipLoaderCommon color="#31B4B9" size={35} margin={5} />
                        ) : (
                            "Pay now"
                        )}
                    </PaymentButton>
                ) : (
                    <PaymentButton
                        $borderRadius="4px"
                        color={["#436CB2 0%", "#28B7C3 100%"]}
                        onClick={() => handleCreateOrder("email-quote")}
                        disabled={!isAgreeTerm || isOrderInProgress}
                    >
                        {isOrderInProgress ? (
                            <ClipLoaderCommon color="#31B4B9" size={35} margin={5} />
                        ) : (
                            SHARE_OPTION
                        )}
                    </PaymentButton>
                )}
            </ListOptions>
        </div>
    );
};

interface IProps {
    items: ICartItemData[];
    handleOrderCreate: (paymentType: string) => Promise<void>;
    onOrderCreateSuccess: () => void;
}

export const CheckoutStep3 = (props: IProps) => {
    return (
        <Container>
            <PaymentOptionComponent {...props} />
        </Container>
    );
};

const Container = styled.div`
    background-color: #f2f2f2;
    padding: 16px 16px 0 16px;
    height: 100%;
    flex: 1;
    width: 100%;

    .payment-wrapper {
        display: flex;
        flex-direction: column;
        align-items: stretch;

        ${breakpoint.breakTablet`
            flex-direction: row-reverse;
            align-items: flex-start;
        `}
    }
`;

const ListOptions = styled.div`
    .MuiRadio-colorSecondary.Mui-checked {
        color: ${(props) => props.theme.colors.main} !important;
    }
    .MuiTypography-body1 {
        font-family: DM Sans;
        font-size: 18px;
        font-weight: 500;
        line-height: 29px;

        ${breakpoint.breakTablet`
      font-size: 14px;
    `}
    }
    .PrivateSwitchBase-root-1 {
        padding: 9px 12px;
    }
    .checkBox {
        display: flex;
        align-items: center;
    }
    .checkBoxText {
        font-size: 13px;
        font-weight: normal;
        line-height: 18px;
        color: ${(p) => p.theme.colors.gray_2};
        position: relative;
        top: -5px;

        ${breakpoint.breakIpadPro`
                font-size: 14px;
            `}

        ${breakpoint.breakLarge`
                .checkBoxText {
                    margin-left: 14px;
                }
            `}
    }
    a {
        color: ${(props) => props.theme.colors.main};
    }
`;

const PaymentButton = styled(StyledButton)`
    margin-bottom: 45px;
    margin-top: 20px;

    @media (min-width: ${(p) => p.theme.breakPoints.breakTablet}) and (max-width: ${(p) =>
            p.theme.breakPoints.breakLarge}) {
        height: 40px;
        margin-bottom: 20px;
        margin-top: 15px;
    }
`;
